import { useRef, type KeyboardEvent } from 'react';

/**
 * Roving tabindex + arrow navigation for a hand-rolled WAI-ARIA tablist (LR-587).
 *
 * One tab stop into the strip — the selected tab is the only focusable one — and
 * ArrowLeft / ArrowRight move focus AND selection, wrapping at the ends.
 *
 * Shared rather than written per strip because Collateral Manager stacks TWO
 * tablists (agreement over leg) in different visual idioms, and the keyboard
 * contract is the half that goes missing when a strip is styled by hand:
 * `role="tab"` + `aria-selected` alone leaves every tab in the tab order and no
 * way to move between them, which is the WCAG AA gap CLAUDE.md calls out.
 */
export const useRovingTabs = <K extends string>(
  keys: readonly K[],
  onChange: (key: K) => void,
) => {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const onKeyDown = (event: KeyboardEvent, index: number): void => {
    // Home/End alongside the arrows: the APG lists all four for a tablist, and a
    // strip that grows past two tabs needs the jump.
    const next =
      event.key === 'ArrowRight'
        ? (index + 1) % keys.length
        : event.key === 'ArrowLeft'
          ? (index - 1 + keys.length) % keys.length
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? keys.length - 1
              : null;
    if (next === null) return;
    event.preventDefault();
    onChange(keys[next]);
    refs.current[next]?.focus();
  };

  return { refs, onKeyDown };
};
